import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

/**
 * 「做到幾成了」的輸入。
 *
 * 這個系統裡有三個地方要填進度：個人工項的回報卡、工項詳情的快速回報、
 * 以及專案經理的代回報。三個地方本來各寫各的 —— 個人工項是五顆大按鈕，
 * 另外兩個是一格「只要填數字，例如 80」的輸入框。同一件事在同一套系統裡
 * 有兩種填法，使用者換一個畫面就要重學一次，而且會以為那是兩件不同的事。
 *
 * 所以抽成一份共用的，以個人工項那一版為準（設計稿 el-pm-template_1.html
 * L1191-L1197）：五顆大按鈕是主要輸入 —— 取代滑桿與下拉，長輩點得到。
 *
 * 五段會失去任意百分比的精度，所以另外留一格「自己填」。目前的值不在五段上
 * （例如 40%）時會自動展開，不必先找到那顆鈕。
 *
 * 值的語意跟三個呼叫端原本就一致，不要改：
 *   value === ""  ＝ 這次不動進度（送出時沿用 currentPct）
 *   其他          ＝ 要寫進去的百分比字串
 */

/** 五段的值與字樣（設計稿 L1191-L1197） */
export const PROGRESS_CHOICES = [
  [0, "尚未開始"],
  [25, "初步進行"],
  [50, "完成一半"],
  [75, "接近完成"],
  [100, "全部完成"],
];

export default function ProgressChoice({ id, label, help, value, currentPct, onChange, disabled }) {
  /* 「自己填」是否展開。值不在五段上時一律展開，所以這只是「使用者自己按開的」 */
  const [custom, setCustom] = useState(false);

  const pctValue =
    value === "" || value == null
      ? currentPct
      : Math.max(0, Math.min(100, Number(value) || 0));
  const isChoice = PROGRESS_CHOICES.some(([v]) => v === pctValue);
  const showCustom = custom || !isChoice;

  return (
    <Box>
      <Typography component="span" sx={{ fontWeight: 600, display: "block" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {PROGRESS_CHOICES.map(([v, text]) => {
          const on = pctValue === v;
          return (
            <Button
              key={v}
              variant={on ? "contained" : "outlined"}
              disabled={disabled}
              aria-pressed={on}
              onClick={() => {
                setCustom(false);
                onChange(String(v));
              }}
              sx={{
                flex: "1 1 110px",
                minWidth: 96,
                minHeight: 60,
                flexDirection: "column",
                gap: 0.25,
                px: 1.5,
                py: 1,
                whiteSpace: "normal",
                // 大字模式按鈕跟著長高、加寬，跟原本的 .el-choice 一樣
                "[data-textsize='lg'] &": { minHeight: 76, flexBasis: 140 },
              }}
            >
              {text}
              <Typography component="small" variant="body2" sx={{ fontWeight: 400, color: "inherit" }}>
                {v}%
              </Typography>
            </Button>
          );
        })}
      </Box>

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 1 }}>
        <Button
          variant="text"
          disabled={disabled}
          aria-pressed={showCustom}
          onClick={() => {
            if (!showCustom) {
              setCustom(true);
              return;
            }
            /*
             * 要收起來的前提是目前的值剛好落在五段上，否則輸入格會因為
             * 「值不在五段上」再自己打開 —— 按下去畫面沒反應的鈕最難懂。
             * 所以按了就順手選最接近的那一段，使用者要的本來就是
             * 「不要自己填了，用上面的大按鈕」。
             */
            if (!isChoice) {
              const near = PROGRESS_CHOICES.reduce(
                (a, [v]) => (Math.abs(v - pctValue) < Math.abs(a - pctValue) ? v : a),
                PROGRESS_CHOICES[0][0]
              );
              onChange(String(near));
            }
            setCustom(false);
          }}
        >
          {showCustom ? "改用上方選項" : "自訂百分比"}
        </Button>
        {showCustom && (
          <>
            <Typography component="label" variant="body2" color="text.secondary" htmlFor={id}>
              輸入百分比（0～100）
            </Typography>
            <TextField
              placeholder="0～100"
              id={id}
              type="number"
              disabled={disabled}
              value={value === "" || value == null ? String(currentPct) : value}
              onChange={(e) => onChange(e.target.value)}
              slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
              sx={{ width: "7em" }}
            />
          </>
        )}
      </Stack>
      {help ? <FormHelperText component="p">{help}</FormHelperText> : null}
    </Box>
  );
}

ProgressChoice.propTypes = {
  /** 「自己填」那一格輸入框的 id，要跟它的 <label> 對得起來 */
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  help: PropTypes.string,
  /** 受控值。"" 代表這次不動進度 */
  value: PropTypes.string,
  /** 這件工項目前的進度，value 為 "" 時顯示的就是它 */
  currentPct: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ProgressChoice.defaultProps = {
  label: "目前進度",
  help: "",
  value: "",
  currentPct: 0,
  disabled: false,
};
